<template>
	<div class="ks-text-field" :class="containerClasses">
		<label v-if="label != null" class="ks-text-field-label" :class="{ 'ks-text-field-label-active': isLabelActive }">{{ label }}</label>
		<div class="ks-text-field-slot">
			<span v-if="prependIcon || loading" class="ks-text-field-prepend" @click="onPrependClick">
				<span v-if="loading" class="ks-text-field-spinner"></span>
				<span v-else class="material-icons ks-text-field-icon">{{ prependIcon }}</span>
			</span>
			<input
				v-if="!textarea"
				ref="input"
				class="ks-input-field"
				:class="inputClasses"
				:type="type"
				:value="lazyValue"
				:disabled="disabled"
				:readonly="readonly"
				:placeholder="placeholder"
				:autofocus="autofocus"
				:autocorrect="autocorrect"
				:autocapitalize="autocapitalize"
				:spellcheck="spellcheck"
				@input="onInput"
				@change="onChange"
				@focus="onFocus"
				@blur="onBlur"
				@keydown.enter="$emit('keydown-enter', $event)"
				@keydown.esc="onEsc"
				v-on="inputListeners"
			/>
			<textarea
				v-else
				ref="input"
				class="ks-input-field ks-text-field-textarea"
				:class="inputClasses"
				:value="lazyValue"
				:disabled="disabled"
				:readonly="readonly"
				:placeholder="placeholder"
				:rows="rows"
				:autofocus="autofocus"
				:autocorrect="autocorrect"
				:autocapitalize="autocapitalize"
				:spellcheck="spellcheck"
				@input="onInput"
				@change="onChange"
				@focus="onFocus"
				@blur="onBlur"
				v-on="inputListeners"
			></textarea>
			<span v-if="appendIcon || clearable" class="ks-text-field-append">
				<span v-if="clearable && lazyValue" class="ks-text-field-icon ks-text-field-clear material-icons" @click="clear">close</span>
				<span v-if="appendIcon" class="material-icons ks-text-field-icon" @click="onAppendClick">{{ appendIcon }}</span>
			</span>
		</div>
		<div v-if="hint && (persistentHint || isFocused)" class="ks-text-field-hint">{{ hint }}</div>
		<div v-if="hasAppend" class="ks-text-field-append"><slot name="append"></slot></div>
		<div v-if="errorMessagesStr != ''" class="ks-text-field-error">{{ errorMessagesStr }}</div>
	</div>
</template>

<script>
export default {
	props: {
		"value": {
			type: [String, Number],
			default: ""
		},
		"label": {
			type: String,
			default: null
		},
		"placeholder": {
			type: String,
			default: ""
		},
		"type": {
			type: String,
			default: "text"
		},
		"disabled": {
			type: Boolean,
			default: false
		},
		"readonly": {
			type: Boolean,
			default: false
		},
		"clearable": {
			type: Boolean,
			default: false
		},
		"autofocus": {
			type: Boolean,
			default: false
		},
		"autocorrect": {
			type: String,
			default: "off"
		},
		"autocapitalize": {
			type: String,
			default: "off"
		},
		"spellcheck": {
			type: Boolean,
			default: false
		},
		"textarea": {
			type: Boolean,
			default: false
		},
		"rows": {
			type: [Number, String],
			default: 3
		},
		"prependIcon": {
			type: String,
			default: ""
		},
		"loading": {
			type: Boolean,
			default: false
		},
		"appendIcon": {
			type: String,
			default: ""
		},
		"prependIconCb": {
			type: Function,
			default: null
		},
		"appendIconCb": {
			type: Function,
			default: null
		},
		"hint": {
			type: String,
			default: ""
		},
		"persistentHint": {
			type: Boolean,
			default: false
		},
		"errorMessages": {
			type: [String , Array],
			default: ""
		},
		"hideDetails": {
			type: Boolean,
			default: false
		},
		"solo": {
			type: Boolean,
			default: false
		},
		"outlined": {
			type: Boolean,
			default: false
		},
		"dense": {
			type: Boolean,
			default: false
		},
		// Emit "input" only on native change (blur/enter), like v-model.lazy
		"lazy": {
			type: Boolean,
			default: false
		}
	},
	data () {
		return {
			lazyValue: this.value,
			isFocused: false
		}
	},
	watch: {
		value (val) {
			this.lazyValue = val
		}
	},
	computed: {
		"hasAppend" () {
			return !!this.$slots.append;
		},
		"errorMessagesStr" () {
			if (Array.isArray(this.errorMessages)) {
				return this.errorMessages.join(", ");
			}
			return this.errorMessages;
		},
		"isLabelActive" () {
			return this.isFocused || !!this.lazyValue || !!this.placeholder
		},
		"containerClasses" () {
			return {
				"ks-text-field-focused": this.isFocused,
				"ks-text-field-disabled": this.disabled,
				"ks-text-field-has-error": this.errorMessagesStr != "",
				"ks-text-field-solo": this.solo,
				"ks-text-field-outlined": this.outlined,
				"ks-text-field-dense": this.dense,
				"ks-text-field-hide-details": this.hideDetails
			}
		},
		"inputClasses" () {
			return {
				"ks-input-sm": this.dense
			}
		},
		"inputListeners" () {
			var vm = this
			var listeners = Object.assign({}, this.$listeners)
			delete listeners.input
			delete listeners.change
			return listeners
		}
	},
	methods: {
		onInput (e) {
			this.lazyValue = e.target.value
			if (!this.lazy) this.$emit("input", this.lazyValue)
		},
		onChange (e) {
			if (this.lazy) this.$emit("input", e.target.value)
			this.$emit("change", e)
		},
		onFocus (e) {
			this.isFocused = true
			this.$emit("focus", e)
		},
		onBlur (e) {
			this.isFocused = false
			this.$emit("blur", e)
		},
		onPrependClick (e) {
			if (this.prependIconCb) this.prependIconCb(e)
			this.$emit("click:prepend", e)
		},
		onAppendClick (e) {
			if (this.appendIconCb) this.appendIconCb(e)
			this.$emit("click:append", e)
		},
		onEsc (e) {
			if (this.clearable) {
				this.clear()
			}
		},
		clear () {
			this.lazyValue = ""
			this.$emit("input", "")
			this.$emit("click:clear")
			this.$nextTick(() => {
				if (this.$refs.input) this.$refs.input.focus()
			})
		},
		focus () {
			if (this.$refs.input) this.$refs.input.focus()
		},
		blur () {
			if (this.$refs.input) this.$refs.input.blur()
		}
	}
}
</script>

<style>
.ks-text-field {
	position: relative;
	margin-bottom: 8px;
}

.ks-text-field-hide-details {
	margin-bottom: 0;
}

.ks-text-field-label {
	display: block;
	font-size: 12px;
	color: var(--ks-font-color-input-label);
	margin-bottom: 4px;
	height: 1em;
	transition: color 0.15s ease;
}
.ks-text-field-focused .ks-text-field-label {
	color: var(--ks-col-accent);
}
.ks-text-field-has-error .ks-text-field-label {
	color: var(--ks-col-danger);
}

.ks-text-field-slot {
	display: flex;
	align-items: center;
	position: relative;
	border-bottom: 2px solid var(--ks-font-color-accent-op30);
	min-height: 24px;
}
.ks-text-field-slot:hover {
	border-bottom-color: var(--ks-font-color-accent-op60);
}
.ks-text-field-focused .ks-text-field-slot {
	border-bottom-color: var(--ks-col-accent);
}
.ks-text-field-has-error .ks-text-field-slot {
	border-bottom-color: var(--ks-col-danger);
}

.ks-text-field-slot .ks-input-field {
	flex: 1;
	min-width: 0;
	border-bottom: none;
	padding-left: 2px;
	padding-top: 6px;
	padding-bottom: 6px;
}
.ks-text-field-slot .ks-input-field:hover {
	border-bottom: none;
}
.ks-text-field-slot .ks-input-field:focus {
	border-bottom: none;
}

.ks-text-field-prepend,
.ks-text-field-append {
	display: inline-flex;
	align-items: center;
}
.ks-text-field-prepend {
	margin-right: 6px;
}
.ks-text-field-append {
	margin-left: 0px;
}

.ks-text-field-spinner {
	width: 16px;
	height: 16px;
	margin: 2px;
	box-sizing: border-box;
	border: 2px solid var(--ks-font-color-4);
	border-top-color: transparent;
	border-radius: 50%;
	animation: ks-text-field-spin 0.6s linear infinite;
}
@keyframes ks-text-field-spin {
	to { transform: rotate(360deg); }
}

.ks-text-field-icon {
	font-size: 20px;
	color: var(--ks-font-color-4);
	cursor: pointer;
	transition: color 0.15s ease;
}
.ks-text-field-icon:hover {
	color: var(--ks-font-color);
}
.ks-text-field-focused .ks-text-field-icon {
	color: var(--ks-col-accent);
}
.ks-text-field-focused .ks-text-field-icon:hover {
	color: var(--ks-col-accent-dark);
}

.ks-text-field-clear {
	font-size: 18px;
	margin-right: 2px;
}

.ks-text-field-textarea {
	resize: vertical;
	height: auto;
	min-height: 60px;
}

.ks-text-field-hint {
	font-size: 11px;
	color: var(--ks-font-color-4);
	margin-top: 4px;
}

.ks-text-field-error {
	font-size: 11px;
	color: var(--ks-col-danger);
	margin-top: 4px;
}
.ks-text-field-has-error .ks-input-field {
	border-bottom-color: transparent;
}

/* Solo variant */
.ks-text-field-solo .ks-text-field-slot {
	border-bottom: none;
}
.ks-text-field-solo .ks-text-field-slot:hover {
	border-bottom: none;
}
.ks-text-field-solo .ks-input-field {
	background: var(--ks-grey-4);
	border: 1px solid transparent;
	border-bottom: 1px solid transparent;
	border-radius: 4px;
	padding: 8px 12px;
}
.ks-text-field-solo .ks-input-field:focus {
	border: 1px solid var(--ks-col-accent);
	border-bottom: 1px solid var(--ks-col-accent);
}

/* Outlined variant */
.ks-text-field-outlined .ks-text-field-slot {
	border-bottom: none;
}
.ks-text-field-outlined .ks-text-field-slot:hover {
	border-bottom: none;
}
.ks-text-field-outlined .ks-input-field {
	background: transparent;
	border: 1px solid var(--ks-grey-4);
	border-bottom: 1px solid var(--ks-grey-4);
	border-radius: 4px;
}
.ks-text-field-outlined .ks-input-field:focus {
	border: 1px solid var(--ks-col-accent);
	border-bottom: 1px solid var(--ks-col-accent);
}

/* Dense */
.ks-text-field-dense {
	margin-bottom: 4px;
}

/* Disabled */
.ks-text-field-disabled {
	opacity: 0.5;
	pointer-events: none;
}
</style>